import { useMemo, useState } from "react";
import type { Book, EntityType, ReviewEntity } from "../api";
import { useAuth } from "../auth";
import { ENTITY_TYPES, ErrorNote, Spinner, useLoad } from "../ui";
import { ReviewBookmark, useReviewBookmark } from "./StudioPage";

const SINGULAR: Record<EntityType, string> = {
  character: "Personage",
  place: "Plaats",
  object: "Voorwerp",
  group: "Groep",
};

export function EntitiesPanel({ book }: { book: Book }) {
  const { api } = useAuth();
  const [bookmark, setBookmark] = useReviewBookmark(book);
  const list = useLoad(() => api.reviewEntities(book.id, bookmark), [api, book.id, bookmark]);
  const [type, setType] = useState<EntityType | "all">("all");
  const [query, setQuery] = useState("");
  const [showHidden, setShowHidden] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<number | null>(null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (list.data ?? []).filter(
      (e) =>
        (type === "all" || e.entity_type === type) &&
        (showHidden || !e.hidden) &&
        (!q || e.name.toLowerCase().includes(q) || e.aliases.some((a) => a.toLowerCase().includes(q))),
    );
  }, [list.data, type, query, showHidden]);

  async function update(e: ReviewEntity, body: Parameters<typeof api.updateEntity>[2]) {
    setError(null);
    try {
      await api.updateEntity(book.id, e.entity_id, body);
      list.setData(
        (list.data ?? []).map((x) =>
          x.entity_id === e.entity_id
            ? { ...x, ...body, name: body.name ?? x.name, entity_type: body.entity_type ?? x.entity_type }
            : x,
        ),
      );
      setRenaming(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  if (list.error) return <ErrorNote error={list.error} />;
  if (!list.data) return <Spinner />;
  return (
    <div className="stack">
      <ReviewBookmark book={book} value={bookmark} onChange={setBookmark} />
      <div className="row filters">
        <select value={type} onChange={(e) => setType(e.target.value as EntityType | "all")}
                aria-label="Soort">
          <option value="all">Alle soorten</option>
          {ENTITY_TYPES.map((t) => (
            <option key={t.type} value={t.type}>
              {t.label}
            </option>
          ))}
        </select>
        <input type="search" placeholder="Zoek op naam of alias…" value={query}
               onChange={(e) => setQuery(e.target.value)} aria-label="Zoeken" />
        <label className="check">
          <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
          <span>Verborgen tonen</span>
        </label>
      </div>
      <p className="muted small">
        Verbergen haalt een entiteit met al haar feiten en relaties weg voor lezers (bijv. onzin als
        "de brief van Lydia"). Hernoemen onthult een naam nooit eerder dan het boek dat doet.
      </p>
      <ErrorNote error={error} />
      <div className="table-wrap">
        <table className="entities-table">
          <thead>
            <tr>
              <th>Naam</th>
              <th>Soort</th>
              <th className="num">Vanaf</th>
              <th className="num">Feiten</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.entity_id} className={e.hidden ? "row-hidden" : ""}>
                <td>
                  {renaming === e.entity_id ? (
                    <RenameForm name={e.name} onSave={(name) => void update(e, { name })}
                                onCancel={() => setRenaming(null)} />
                  ) : (
                    <>
                      <strong>{e.name}</strong>
                      {e.aliases.length > 0 && <div className="muted small">{e.aliases.join(", ")}</div>}
                    </>
                  )}
                </td>
                <td>
                  <select value={e.entity_type} aria-label={`Soort van ${e.name}`}
                          onChange={(ev) => void update(e, { entity_type: ev.target.value as EntityType })}>
                    {ENTITY_TYPES.map((t) => (
                      <option key={t.type} value={t.type}>
                        {SINGULAR[t.type]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="num">H{e.first_seen_chapter}</td>
                <td className="num" title="zichtbaar / te controleren">
                  {e.verified}
                  {e.not_verified > 0 && <span className="muted"> / {e.not_verified}</span>}
                </td>
                <td>
                  <div className="row">
                    <button className="btn btn-ghost small" onClick={() => setRenaming(e.entity_id)}>
                      Hernoemen
                    </button>
                    <button
                      className={`btn small ${e.hidden ? "" : "btn-ghost btn-danger"}`}
                      onClick={() => void update(e, { hidden: !e.hidden })}
                    >
                      {e.hidden ? "Terugzetten" : "Verbergen"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RenameForm({
  name,
  onSave,
  onCancel,
}: {
  name: string;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(name);
  return (
    <form className="row" onSubmit={(e) => { e.preventDefault(); if (value.trim()) onSave(value.trim()); }}>
      <input value={value} onChange={(e) => setValue(e.target.value)} autoFocus aria-label="Nieuwe naam"
             maxLength={200} />
      <button className="btn small" type="submit">
        Opslaan
      </button>
      <button className="btn btn-ghost small" type="button" onClick={onCancel}>
        Annuleren
      </button>
    </form>
  );
}
